// v1 → v2 migrator for the page model. Idempotent: running twice is a
// no-op. v1 readers don't see `sections`/`pageSchemaVersion`; v2 readers
// always operate on the migrated shape returned by `toV2(model)`.

import type {
  Block, ColorScheme, FontFamilyPreset, PageKind, PageMode, PageModel, PageTheme, Section, SectionPreset, BlockVisibility,
} from './types'
import { PAGE_SCHEMA_VERSION } from './types'
import { migrateTodoBlock, migrateListBlock } from './blocks'
import { getPreset } from './theme'

let _seq = 0
const newId = (prefix: string): string => {
  _seq += 1
  return `${prefix}_${Date.now().toString(36)}_${_seq}`
}

export const newSectionId = (): string => newId('sec')
export const newVariantId = (): string => newId('var')
export const newPresetId  = (): string => newId('preset')

/**
 * Normalise an incoming page model into the canonical v2 shape:
 *   - sections is the source of truth
 *   - the legacy flat `blocks` is treated as a transient editor mirror
 *     of the **main** section's blocks
 *
 * If the input has sections, blocks is hoisted from the main section
 * (overwriting any stray top-level blocks). If the input has only a
 * flat blocks array, it is wrapped into a single Main section.
 */
export function toV2(model: PageModel): PageModel {
  let normalized: PageModel
  if (model.sections && model.sections.length > 0) {
    const sections = model.sections.map(sec => ({
      ...sec,
      blocks: migrateBlockList(sec.blocks),
    }))
    const main = sections.find(s => (s.tag ?? 'main') === 'main')
    normalized = {
      ...model,
      pageSchemaVersion: 2,
      sections,
      blocks: main?.blocks ?? [],
    }
  } else {
    const blocks: Block[] = migrateBlockList(Array.isArray(model.blocks) ? model.blocks : [])
    const section: Section = {
      id: newSectionId(),
      name: 'Main',
      tag: 'main',
      blocks,
    }
    normalized = {
      ...model,
      pageSchemaVersion: 2,
      sections: [section],
      blocks,
    }
  }
  return normalizeThemeAndTokens(normalizePageKind(normalized))
}

/**
 * Resolve the page's `kind` from either the explicit field or the legacy
 * `pageMode`. Idempotent.
 *
 * Mapping for legacy pages (no `kind` set):
 *   pageMode === 'document' → kind = 'document'
 *   anything else / absent  → kind = 'internal'
 *
 * The `pageMode` field is also kept in sync (derived from `kind`) so any
 * un-migrated reader sees a consistent value during the deprecation
 * window. Once `pageMode` consumers are gone, this mirror can drop.
 */
export function normalizePageKind(model: PageModel): PageModel {
  const resolved: PageKind = model.kind
    ?? (model.pageMode === 'document' ? 'document' : 'internal')
  // Mirror back into pageMode for legacy readers. 'external' has no
  // pageMode equivalent — leave pageMode as 'web' (closest legacy
  // semantics: stateless render) so old code doesn't fall into the
  // document branch.
  const mirrored: PageMode = resolved === 'document' ? 'document' : 'web'
  if (model.kind === resolved && model.pageMode === mirrored) return model
  return { ...model, kind: resolved, pageMode: mirrored }
}

/**
 * Fold the deprecated DesignTokens defaults into PageTheme, then strip them.
 *
 * History: DesignTokens used to carry `defaultColorSchemeId` /
 * `defaultTypographyId` and a typography library; the runtime applied them
 * page-wide on top of `PageTheme`, creating two parallel page-wide color
 * systems. We now keep page-wide colors/typography only on PageTheme; the
 * `colorSchemes` library is reserved for per-section overrides.
 *
 * Idempotent: re-running on already-migrated data is a no-op.
 */
export function normalizeThemeAndTokens(model: PageModel): PageModel {
  const tokens = model.designTokens as (PageModel['designTokens'] & {
    defaultColorSchemeId?: string
    defaultTypographyId?: string
    typographies?: Array<{
      id: string
      bodyFamily?: string
      headingFamily?: string
      baseSize?: number
      scaleRatio?: number
      lineHeight?: number
      weightBody?: number
    }>
  }) | undefined
  if (!tokens) return model

  let theme: PageTheme = model.theme ? { ...model.theme } : {}
  const presetTheme = getPreset(theme.preset).theme

  // ── Color scheme default → page palette ────────────────────────
  if (tokens.defaultColorSchemeId) {
    const scheme = (tokens.colorSchemes ?? []).find(s => s.id === tokens.defaultColorSchemeId)
    if (scheme && palettesEqualOrEmpty(theme.palette, presetTheme.palette)) {
      theme = {
        ...theme,
        palette: schemeToPalette(scheme),
      }
    }
  }

  // ── Typography set default → page typography ───────────────────
  if (tokens.defaultTypographyId) {
    const typo = (tokens.typographies ?? []).find(t => t.id === tokens.defaultTypographyId)
    if (typo && typographyEqualOrEmpty(theme.typography, presetTheme.typography)) {
      theme = {
        ...theme,
        typography: {
          fontFamily: stringToFontPreset(typo.bodyFamily),
          headingFamily: stringToFontPreset(typo.headingFamily),
          baseSize: typo.baseSize,
          scaleRatio: typo.scaleRatio,
          lineHeight: typo.lineHeight,
          fontWeight: typo.weightBody,
        },
      }
    }
  }

  // Drop deprecated fields. Keep colorSchemes (the section-override library).
  const { defaultColorSchemeId: _a, defaultTypographyId: _b, typographies: _c, ...keepTokens } = tokens
  const designTokens = Object.keys(keepTokens).length > 0 ? keepTokens : undefined

  return { ...model, theme, designTokens }
}

function schemeToPalette(s: ColorScheme): NonNullable<PageTheme['palette']> {
  return {
    primary: s.primary,
    accent: s.accent,
    text: s.text,
    textSecondary: s.textSecondary,
    background: s.background,
    surface: s.surface,
    border: s.border,
  }
}

function palettesEqualOrEmpty(
  current: PageTheme['palette'] | undefined,
  presetDefault: PageTheme['palette'] | undefined,
): boolean {
  if (!current || Object.keys(current).length === 0) return true
  if (!presetDefault) return false
  for (const key of Object.keys(current) as Array<keyof NonNullable<PageTheme['palette']>>) {
    if ((current as Record<string, unknown>)[key] !== (presetDefault as Record<string, unknown>)[key]) {
      return false
    }
  }
  return true
}

function typographyEqualOrEmpty(
  current: PageTheme['typography'] | undefined,
  presetDefault: PageTheme['typography'] | undefined,
): boolean {
  if (!current || Object.keys(current).length === 0) return true
  if (!presetDefault) return false
  for (const key of Object.keys(current) as Array<keyof NonNullable<PageTheme['typography']>>) {
    if ((current as Record<string, unknown>)[key] !== (presetDefault as Record<string, unknown>)[key]) {
      return false
    }
  }
  return true
}

// Map a free-form font-family string (from old TypographyTokenSet) to the
// closest `FontFamilyPreset` used by PageTheme.typography. Defaults to
// `'inter'` when nothing matches.
function stringToFontPreset(family: string | undefined): FontFamilyPreset {
  if (!family) return 'inter'
  const f = family.toLowerCase()
  if (f.includes('inter')) return 'inter'
  if (f.includes('roboto')) return 'roboto'
  if (f.includes('poppins')) return 'poppins'
  if (f.includes('playfair')) return 'playfair'
  if (f.includes('merriweather')) return 'merriweather'
  if (f.includes('mono') || f.includes('menlo') || f.includes('consolas')) return 'mono'
  if (f.includes('serif') || f.includes('georgia') || f.includes('times')) return 'serif'
  if (f.includes('system') || f.includes('-apple-system') || f.includes('segoe')) return 'system'
  return 'sans'
}

// Generators (and the change agent in particular) sometimes emit a
// container block with the wrong shape — e.g. an `accordion` with a flat
// top-level `blocks: [...]` and ad-hoc fields like `title`/`collapsible`/
// `defaultExpanded` instead of the canonical
// `items: [{ id, title, defaultOpen?, blocks?: [...] }]` shape.
//
// Coerce that drift into a single-section accordion/tabs so the editor
// renders instead of crashing on `block.items.forEach` / `.length`. The
// canonical shape is preserved verbatim — this only fires when `items`
// is missing/empty AND a top-level `blocks` array is present.
function coerceAccordionDrift(b: any): any {
  if (Array.isArray(b.items) && b.items.length > 0) return b
  if (Array.isArray(b.blocks) && b.blocks.length > 0) {
    const item = {
      id: typeof b.id === 'string' ? `${b.id}_item_1` : newId('acc'),
      title: typeof b.title === 'string' && b.title ? b.title : 'Section 1',
      defaultOpen: b.defaultOpen === true || b.defaultExpanded === true,
      blocks: b.blocks,
    }
    const {
      blocks: _blocks, title: _t, defaultExpanded: _d, collapsible: _c,
      allowMinimize: _m, ...rest
    } = b
    return { ...rest, items: [item] }
  }
  // Last-resort: ensure items is always an array so AccordionCanvas does
  // not crash on `block.items.forEach` / `.length`.
  return { ...b, items: [] }
}

function coerceTabsDrift(b: any): any {
  if (Array.isArray(b.items) && b.items.length > 0) return b
  if (Array.isArray(b.blocks) && b.blocks.length > 0) {
    const item = {
      id: typeof b.id === 'string' ? `${b.id}_tab_1` : newId('tab'),
      label: typeof b.title === 'string' && b.title ? b.title : 'Tab 1',
      blocks: b.blocks,
    }
    const { blocks: _blocks, title: _t, ...rest } = b
    return { ...rest, items: [item] }
  }
  return { ...b, items: [] }
}

// Apply per-block schema migrations (currently: legacy todo → list shape).
// Recurses into nested children so todos inside columns/containers also
// get upgraded.
function migrateBlockList(blocks: Block[]): Block[] {
  return blocks.map(b => {
    let next: Block = b
    const t: string = (b as { type?: string }).type ?? ''
    if (t === 'accordion') {
      next = coerceAccordionDrift(b) as Block
    } else if (t === 'tabs') {
      next = coerceTabsDrift(b) as Block
    }
    if (t === 'todo') {
      next = migrateTodoBlock(b)
    } else if (t === 'bullet_list' || t === 'numbered_list') {
      next = migrateListBlock(b as any)
    } else if (t === 'bullet_item') {
      // Legacy block type from before the multi-item rename. Coerce to
      // 'bullet_list' (and wrap text/indent into items[]) so older saved
      // pages continue to render.
      next = { ...migrateListBlock({ ...b, type: 'bullet_list' } as any), type: 'bullet_list' } as Block
    } else if (t === 'numbered_item') {
      next = { ...migrateListBlock({ ...b, type: 'numbered_list' } as any), type: 'numbered_list' } as Block
    } else if (t === 'embed') {
      // Generators (and some hand-authored pages) sometimes use `type: "embed"`
      // for an embedded video — populating `provider`/`url` instead of the
      // designer's transaction-embed `embed: { obj_name, ... }` payload. Coerce
      // those to a proper `video` block so the canvas + label code don't crash
      // dereferencing `block.embed.obj_name`.
      const raw = b as any
      const hasVideoShape = !raw.embed && (typeof raw.url === 'string' || typeof raw.provider === 'string')
      if (hasVideoShape) {
        next = {
          id: raw.id,
          type: 'video',
          provider: raw.provider === 'vimeo' ? 'vimeo' : raw.provider === 'file' ? 'file' : 'youtube',
          url: typeof raw.url === 'string' ? raw.url : '',
          aspect: raw.aspect === '4:3' || raw.aspect === '1:1' ? raw.aspect : '16:9',
          caption: typeof raw.caption === 'string' ? raw.caption : undefined,
        } as Block
      }
    } else if (t === 'image_gallery') {
      // Generator alias for the canonical `gallery` block. Map `images[]` →
      // `items[]` (gallery uses {id, url, caption?}; alt is dropped).
      const raw = b as any
      const items = Array.isArray(raw.images)
        ? raw.images.map((im: any, idx: number) => ({
            id: typeof im?.id === 'string' ? im.id : `img_${idx + 1}`,
            url: typeof im?.url === 'string' ? im.url : '',
            caption: typeof im?.caption === 'string' ? im.caption : undefined,
          }))
        : Array.isArray(raw.items) ? raw.items : []
      const cols = Number(raw.columns)
      next = {
        id: raw.id,
        type: 'gallery',
        items,
        columns: (cols === 1 || cols === 2 || cols === 3 || cols === 4 || cols === 6) ? cols : 3,
      } as Block
    }
    if (Array.isArray((next as any).children) && (next as any).children.length > 0) {
      next = { ...next, children: migrateBlockList((next as any).children) } as Block
    }
    // Recurse into multi-slot containers so legacy todo/list shapes get
    // migrated regardless of how deeply they were nested.
    const nt = (next as any).type
    if (nt === 'tabs' && Array.isArray((next as any).items)) {
      next = {
        ...next,
        items: (next as any).items.map((it: any) =>
          Array.isArray(it.blocks) && it.blocks.length > 0
            ? { ...it, blocks: migrateBlockList(it.blocks) }
            : it,
        ),
      } as Block
    } else if (nt === 'accordion' && Array.isArray((next as any).items)) {
      next = {
        ...next,
        items: (next as any).items.map((it: any) =>
          Array.isArray(it.blocks) && it.blocks.length > 0
            ? { ...it, blocks: migrateBlockList(it.blocks) }
            : it,
        ),
      } as Block
    } else if (nt === 'columns' && Array.isArray((next as any).columns)) {
      next = {
        ...next,
        columns: (next as any).columns.map((c: any) =>
          Array.isArray(c.blocks) && c.blocks.length > 0
            ? { ...c, blocks: migrateBlockList(c.blocks) }
            : c,
        ),
      } as Block
    }
    return next
  })
}

/**
 * Ensure the **main** section's `blocks` array tracks the legacy
 * top-level `model.blocks`. The canvas continues to write to
 * `page.blocks`; this helper propagates those writes into the matching
 * section so v2 readers stay consistent. Idempotent.
 *
 * Call from every editor mutation that touches blocks.
 */
export function ensureMainSection(model: PageModel): PageModel {
  let sections = model.sections ?? []
  let mainIdx = sections.findIndex(s => (s.tag ?? 'main') === 'main')
  if (mainIdx < 0) {
    const main: Section = {
      id: newSectionId(),
      name: 'Main',
      tag: 'main',
      blocks: model.blocks,
    }
    // Place Main after any Header but before any Footer.
    const headerIdx = sections.findIndex(s => s.tag === 'header')
    const insertAt = headerIdx >= 0 ? headerIdx + 1 : 0
    sections = [
      ...sections.slice(0, insertAt),
      main,
      ...sections.slice(insertAt),
    ]
    mainIdx = insertAt
  } else if (sections[mainIdx].blocks !== model.blocks) {
    sections = [...sections]
    sections[mainIdx] = { ...sections[mainIdx], blocks: model.blocks }
  }
  return { ...model, sections, pageSchemaVersion: 2 }
}

/**
 * Save-time mirror: derives `page.blocks` from the **main** section so
 * v1-only consumers continue to render the page body. Header / footer /
 * aside sections are NOT flattened into blocks (they live only in the
 * v2 sections array — that's the point of having sections).
 */
export function syncV1Mirror(model: PageModel): PageModel {
  if (!model.sections || model.sections.length === 0) return model
  const main = model.sections.find(s => (s.tag ?? 'main') === 'main')
  if (!main) return model
  return { ...model, blocks: main.blocks }
}

/**
 * Recursively walk every block in a model (including nested children)
 * and apply a visitor. Used by the editor's search, undo coalescing,
 * and ID stamping passes.
 */
export function walkBlocks(
  model: PageModel,
  visit: (b: Block, parent: Block | Section, path: string[]) => void,
): void {
  const sections = model.sections ?? [{
    id: 'sec_legacy',
    name: 'Main',
    blocks: model.blocks,
  } as Section]

  for (const sec of sections) {
    walkList(sec.blocks, sec, [sec.id], visit)
  }
}

function walkList(
  blocks: Block[],
  parent: Block | Section,
  path: string[],
  visit: (b: Block, parent: Block | Section, path: string[]) => void,
) {
  for (const b of blocks) {
    visit(b, parent, [...path, b.id])
    if (Array.isArray(b.children) && b.children.length > 0) {
      walkList(b.children, b, [...path, b.id], visit)
    }
  }
}

/**
 * Patch a single block by ID anywhere in the section/children tree.
 * Returns a new PageModel — does not mutate.
 */
export function patchBlock(
  model: PageModel,
  blockId: string,
  patch: Partial<Block>,
): PageModel {
  if (!model.sections) {
    const blocks = model.blocks.map(b => (b.id === blockId ? { ...b, ...patch } as Block : b))
    return { ...model, blocks }
  }
  const sections = model.sections.map(sec => ({
    ...sec,
    blocks: patchList(sec.blocks, blockId, patch),
  }))
  return syncV1Mirror({ ...model, sections })
}

function patchList(blocks: Block[], blockId: string, patch: Partial<Block>): Block[] {
  return blocks.map(b => {
    if (b.id === blockId) return { ...b, ...patch } as Block
    if (Array.isArray(b.children)) {
      return { ...b, children: patchList(b.children, blockId, patch) } as Block
    }
    return b
  })
}

/**
 * Insert a section at a given index (default end).
 */
export function insertSection(model: PageModel, section: Section, index?: number): PageModel {
  const sections = [...(model.sections ?? [])]
  const at = typeof index === 'number' ? Math.max(0, Math.min(index, sections.length)) : sections.length
  sections.splice(at, 0, section)
  return syncV1Mirror({ ...model, sections, pageSchemaVersion: 2 })
}

/**
 * Remove a section by ID.
 */
export function removeSection(model: PageModel, sectionId: string): PageModel {
  const sections = (model.sections ?? []).filter(s => s.id !== sectionId)
  return syncV1Mirror({ ...model, sections })
}

/**
 * Move a section to a new index.
 */
export function moveSection(model: PageModel, sectionId: string, toIndex: number): PageModel {
  const sections = [...(model.sections ?? [])]
  const from = sections.findIndex(s => s.id === sectionId)
  if (from < 0) return model
  const [s] = sections.splice(from, 1)
  sections.splice(Math.max(0, Math.min(toIndex, sections.length)), 0, s)
  return syncV1Mirror({ ...model, sections })
}

/**
 * Move a block (within or across sections, optionally into/out of a
 * container's `children` array). Path: ['sectionId', 'blockId', …,
 * 'parentBlockIdOrSectionId']. We accept simpler "by-id" args for the
 * common case.
 */
export interface MoveBlockTarget {
  sectionId: string
  parentBlockId?: string         // when undefined, move into section root
  index: number
}
export function moveBlock(
  model: PageModel,
  blockId: string,
  to: MoveBlockTarget,
): PageModel {
  if (!model.sections) return model
  let removed: Block | null = null
  const stripped = model.sections.map(sec => ({
    ...sec,
    blocks: stripFromList(sec.blocks, blockId, b => { removed = b }),
  }))
  if (!removed) return model
  const inserted = stripped.map(sec => {
    if (sec.id !== to.sectionId) return sec
    if (!to.parentBlockId) {
      const blocks = [...sec.blocks]
      blocks.splice(Math.max(0, Math.min(to.index, blocks.length)), 0, removed!)
      return { ...sec, blocks }
    }
    return { ...sec, blocks: insertIntoChildren(sec.blocks, to.parentBlockId, removed!, to.index) }
  })
  return syncV1Mirror({ ...model, sections: inserted })
}

function stripFromList(blocks: Block[], id: string, take: (b: Block) => void): Block[] {
  const out: Block[] = []
  for (const b of blocks) {
    if (b.id === id) { take(b); continue }
    if (Array.isArray(b.children)) {
      out.push({ ...b, children: stripFromList(b.children, id, take) } as Block)
    } else {
      out.push(b)
    }
  }
  return out
}

function insertIntoChildren(blocks: Block[], parentId: string, child: Block, index: number): Block[] {
  return blocks.map(b => {
    if (b.id === parentId) {
      const children = [...(b.children ?? [])]
      children.splice(Math.max(0, Math.min(index, children.length)), 0, child)
      return { ...b, children } as Block
    }
    if (Array.isArray(b.children)) {
      return { ...b, children: insertIntoChildren(b.children, parentId, child, index) } as Block
    }
    return b
  })
}

/**
 * Visibility evaluation helper used by both editor preview and runtime.
 */
export function isVisible(
  v: BlockVisibility | undefined,
  ctx: { viewport: 'desktop' | 'tablet' | 'mobile'; role?: string; now?: Date; vars?: Record<string, unknown> },
): boolean {
  if (!v) return true
  if (v.devices && v.devices.length > 0 && !v.devices.includes(ctx.viewport)) return false
  if (v.roles && v.roles.length > 0 && (!ctx.role || !v.roles.includes(ctx.role))) return false
  if (v.schedule) {
    const now = ctx.now ?? new Date()
    if (v.schedule.start && new Date(v.schedule.start) > now) return false
    if (v.schedule.end && new Date(v.schedule.end) < now) return false
  }
  if (v.condition && ctx.vars) {
    try {
      const truthy = evalCondition(v.condition, ctx.vars)
      if (!truthy) return false
    } catch { /* ignore — fail open */ }
  }
  return true
}

// Tiny safe expression evaluator for visibility.condition.
// Supports {{var}} substitution + a handful of comparison/arithmetic
// operators. NOT a general JS evaluator — string match only.
function evalCondition(expr: string, vars: Record<string, unknown>): boolean {
  const subbed = expr.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_, key: string) => {
    const v = key.split('.').reduce<any>((acc, k) => (acc == null ? acc : acc[k]), vars)
    if (v == null) return 'null'
    if (typeof v === 'string') return JSON.stringify(v)
    return String(v)
  })
  // Allowed: digits, strings, comparisons, basic logic. Reject anything else.
  if (!/^[\s0-9"'.\-+*/%<>=!&|()\[\]_a-zA-Z,nul]+$/.test(subbed)) return false
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(`return (${subbed});`)
    return Boolean(fn())
  } catch {
    return false
  }
}

/** Unused export — keeps the linter quiet on SectionPreset references. */
export type _SectionPresetMarker = SectionPreset
